import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, 
  Plus, 
  Search, 
  Trash2, 
  Phone,
  Mail,
  Building2,
  MoreVertical,
  FileSpreadsheet,
  Users,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Contacts() {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  
  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [selectedList, setSelectedList] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  
  const [showListDialog, setShowListDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [newListName, setNewListName] = useState("");
  const [newContact, setNewContact] = useState({
    first_name: "", last_name: "", phone: "", email: "", company: ""
  });

  useEffect(() => {
    fetchLists();
    fetchContacts();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [selectedList, statusFilter]);

  const fetchLists = async () => {
    try {
      const res = await axios.get(`${API}/lists`, { headers: getAuthHeader() });
      setLists(res.data);
    } catch (error) {
      console.error("Error fetching lists:", error);
    }
  };

  const fetchContacts = async () => {
    try {
      let url = `${API}/contacts?limit=200`;
      if (selectedList) url += `&list_id=${selectedList}`;
      if (statusFilter && statusFilter !== "all") url += `&status=${statusFilter}`;
      
      const res = await axios.get(url, { headers: getAuthHeader() });
      setContacts(res.data.contacts || []);
      setTotalContacts(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const createList = async () => {
    if (!newListName.trim()) {
      toast.error("Please enter a list name");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("name", newListName);
      await axios.post(`${API}/lists`, formData, { headers: getAuthHeader() });
      toast.success("List created");
      setNewListName("");
      setShowListDialog(false);
      fetchLists();
    } catch (error) {
      toast.error("Failed to create list");
    }
  };

  const deleteList = async (listId) => {
    if (!confirm("Delete this list and all its contacts?")) return;
    try {
      await axios.delete(`${API}/lists/${listId}`, { headers: getAuthHeader() });
      toast.success("List deleted");
      if (selectedList === listId) setSelectedList(null);
      fetchLists();
      fetchContacts();
    } catch (error) {
      toast.error("Failed to delete list");
    }
  };

  const createContact = async () => {
    if (!newContact.first_name || !newContact.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      const formData = new FormData();
      Object.entries(newContact).forEach(([k, v]) => v && formData.append(k, v));
      if (selectedList) formData.append("list_id", selectedList);
      
      await axios.post(`${API}/contacts`, formData, { headers: getAuthHeader() });
      toast.success("Contact added");
      setNewContact({ first_name: "", last_name: "", phone: "", email: "", company: "" });
      setShowContactDialog(false);
      fetchContacts();
      fetchLists();
    } catch (error) {
      toast.error("Failed to add contact");
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`, { headers: getAuthHeader() });
      toast.success("Contact deleted");
      fetchContacts();
      fetchLists();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const uploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedList) {
      toast.error("Please select a list first");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/lists/${selectedList}/upload`, formData, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
      });
      
      toast.success(res.data.message);
      fetchContacts();
      fetchLists();
      setShowUploadDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload contacts");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "qualified": return "bg-emerald-500/20 text-emerald-400";
      case "contacted": return "bg-blue-500/20 text-blue-400";
      case "converted": return "bg-purple-500/20 text-purple-400";
      case "do_not_call": return "bg-red-500/20 text-red-400";
      default: return "bg-gray-500/20 text-gray-400";
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(query) ||
      c.last_name?.toLowerCase().includes(query) ||
      c.phone?.includes(query) ||
      c.company?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-8" data-testid="contacts-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Contacts</h1>
          <p className="text-muted-foreground">Manage your contact lists for campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowListDialog(true)} data-testid="create-list-btn">
            <Plus className="w-4 h-4 mr-2" />
            New List
          </Button>
          <Button onClick={() => setShowContactDialog(true)} data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lists Sidebar */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Contact Lists
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => setSelectedList(null)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    !selectedList ? "bg-primary/10 text-primary" : "hover:bg-secondary"
                  }`}
                  data-testid="all-contacts-btn"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">All Contacts</span>
                    <Badge variant="secondary">{totalContacts}</Badge>
                  </div>
                </button>
                {lists.map((list) => (
                  <div
                    key={list.id}
                    className={`w-full p-3 rounded-lg transition-colors group ${
                      selectedList === list.id ? "bg-primary/10 text-primary" : "hover:bg-secondary"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setSelectedList(list.id)}
                        className="flex-1 text-left"
                        data-testid={`list-${list.id}`}
                      >
                        <span className="font-medium">{list.name}</span>
                      </button>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{list.contact_count}</Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => { setSelectedList(list.id); setShowUploadDialog(true); }}>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteList(list.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Contacts Table */}
        <Card className="lg:col-span-3 bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                {selectedList ? lists.find(l => l.id === selectedList)?.name : "All Contacts"}
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    className="pl-9 w-48"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-contacts"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                  </SelectContent>
                </Select>
                {selectedList && (
                  <Button variant="outline" onClick={() => setShowUploadDialog(true)} data-testid="upload-csv-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload CSV
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => (
                    <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                      <TableCell className="font-medium">
                        {contact.first_name} {contact.last_name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3 text-muted-foreground" />
                          {contact.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 text-muted-foreground" />
                            {contact.email}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.company && (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3 h-3 text-muted-foreground" />
                            {contact.company}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(contact.status)}>
                          {contact.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteContact(contact.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredContacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        No contacts found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Create List Dialog */}
      <Dialog open={showListDialog} onOpenChange={setShowListDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New List</DialogTitle>
            <DialogDescription>Create a list to organize your contacts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>List Name</Label>
              <Input
                placeholder="e.g., Hot Leads, Follow-ups"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                data-testid="list-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowListDialog(false)}>Cancel</Button>
            <Button onClick={createList} data-testid="save-list-btn">Create List</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
            <DialogDescription>Add a new contact manually</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  data-testid="contact-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  data-testid="contact-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                placeholder="+1 555-123-4567"
                data-testid="contact-phone"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                data-testid="contact-email"
              />
            </div>
            <div>
              <Label>Company</Label>
              <Input
                value={newContact.company}
                onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                data-testid="contact-company"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowContactDialog(false)}>Cancel</Button>
            <Button onClick={createContact} data-testid="save-contact-btn">Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Contacts</DialogTitle>
            <DialogDescription>
              Upload a CSV file with your contacts. Required columns: phone. Optional: first_name, last_name, email, company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Drag & drop your CSV file here, or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={uploadCSV}
                className="hidden"
                id="csv-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="choose-file-btn"
              >
                {uploading ? "Uploading..." : "Choose File"}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">CSV Format Example:</p>
              <code className="block bg-secondary/50 p-2 rounded text-xs">
                first_name,last_name,phone,email,company<br/>
                John,Doe,+15551234567,john@email.com,Acme Inc
              </code>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
